import {
  type CameraMedia,
  type CameraMediaKind,
  type CameraMediaTrigger,
  type MachineCamera,
  queryKeys,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Camera } from 'lucide-react'
import { useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { CameraCaptureDialog } from '../components/camera-capture-dialog.js'
import { CameraMediaDialog } from '../components/camera-media-dialog.js'
import { type CameraMediaFilters, CameraMediaGrid } from '../components/camera-media-grid.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const ALL = 'all'

function startOfDay(date: string): string | undefined {
  return date ? new Date(`${date}T00:00:00`).toISOString() : undefined
}

function endOfDay(date: string): string | undefined {
  return date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined
}

/**
 * Snapshots and short clips taken from the cameras on each kiosk, newest
 * first. Anything here can be attached to a support ticket from its viewer.
 */
export function CamerasPage() {
  const { can } = useAuth()
  const canManage = can('kiosks:manage')

  const [machineId, setMachineId] = useState(ALL)
  const [cameraId, setCameraId] = useState(ALL)
  const [kind, setKind] = useState<string>(ALL)
  const [trigger, setTrigger] = useState<string>(ALL)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [capturing, setCapturing] = useState(false)
  const [viewing, setViewing] = useState<CameraMedia | null>(null)

  const { data: machines } = useQuery({
    queryKey: queryKeys.vending.machines(),
    queryFn: () => api.get<VendingMachine[]>('/vending/machines'),
  })
  const { data: cameras } = useQuery({
    queryKey: queryKeys.cameras.machineCameras(machineId),
    queryFn: () => api.get<MachineCamera[]>(`/vending/machines/${machineId}/cameras`),
    enabled: canManage && machineId !== ALL,
  })

  const filters: CameraMediaFilters = {
    machineId: machineId === ALL ? undefined : machineId,
    cameraId: cameraId === ALL ? undefined : cameraId,
    kind: kind === ALL ? undefined : (kind as CameraMediaKind),
    trigger: trigger === ALL ? undefined : (trigger as CameraMediaTrigger),
    from: startOfDay(from),
    to: endOfDay(to),
  }

  return (
    <Page>
      <PageHeader
        title="Cameras"
        description="Snapshots, short video clips, and photos or screen captures taken from your kiosks, including screens captured automatically on checkout errors. Captures are kept for 90 days, unless you attach one to a support ticket."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setCapturing(true)}>
              <Camera /> Capture
            </Button>
          ) : undefined
        }
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="w-48">
          <Select
            value={machineId}
            onValueChange={(value) => {
              setMachineId(value)
              setCameraId(ALL)
            }}
          >
            <SelectTrigger aria-label="Machine">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All machines</SelectItem>
              {(machines ?? []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.status === 'retired' ? `${m.name} (retired)` : m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canManage && machineId !== ALL ? (
          <div className="w-44">
            <Select value={cameraId} onValueChange={setCameraId}>
              <SelectTrigger aria-label="Camera">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All cameras</SelectItem>
                {(cameras ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="w-44">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger aria-label="Type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              <SelectItem value="snapshot">Snapshots</SelectItem>
              <SelectItem value="clip">Video clips</SelectItem>
              <SelectItem value="photo">Customer photos</SelectItem>
              <SelectItem value="screenshot">Screen captures</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={trigger} onValueChange={setTrigger}>
            <SelectTrigger aria-label="Taken by">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any source</SelectItem>
              <SelectItem value="staff">Requested by staff</SelectItem>
              <SelectItem value="checkout_error">Automatic · checkout error</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
          <span aria-hidden="true">From</span>
          <Input
            type="date"
            aria-label="From date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="w-36"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
          <span aria-hidden="true">To</span>
          <Input
            type="date"
            aria-label="To date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="w-36"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        <CameraMediaGrid filters={filters} onSelect={setViewing} />
      </div>

      <CameraCaptureDialog open={capturing} onOpenChange={setCapturing} onCaptured={setViewing} />
      <CameraMediaDialog media={viewing} onClose={() => setViewing(null)} />
    </Page>
  )
}
